import React, { useState } from "react";

import { getGroupOptions } from "@Totum/Main/Components/groups/GroupUtils";
import { TagType } from "@Totum/Main/Components/groups/TagProperties";
import { DefaultDrawDowns, GroupContext, QueryKeys, SystemContext, UserContext } from "@Totum/Main/Constants";
import SessionManager from "@Totum/Main/Infrastructure/session";
import { IActivityDefinitions } from "@Totum/Main/Domain/IActivityDetails";
import { IAddressDetailsPaged } from "@Totum/Main/Domain/IAddressDetails";
import { ICreditDetails, ICreditDetailsPaged } from "@Totum/Main/Domain/ICreditDetails";
import { ILicenseCreationDetails } from "@Totum/Main/Domain/ILicenseDetails";
import { IRoleAssignmentDetails, IRoleAssignmentPaged, IRolePayload } from "@Totum/Main/Domain/IRoleDetails";
import { IGroupAssignmentDetails, IUserGroupDetails } from "@Totum/Main/Domain/IUserGroupDetails";
import { getSystemId, hasCapability } from "@Totum/Main/Utils";
import { PlusCircleOutlined } from "@ant-design/icons";
import { Cascader, Input } from "antd";
import { IAddAction } from "@Totum/Main/Components/groups/types";
import appQueryClient from "@Totum/Main/QueryClient";
import { useQuery } from "@tanstack/react-query";
import UserGroupService from "services/UserGroupService";

interface ICascaderOptions {
    label: string | React.ReactNode;
    value: string;
    children?: ICascaderOptions[];
}

interface IProps {
    groupId?: string;
    page: number;
    creditAssignments?: ICreditDetailsPaged[] | ICreditDetails[];
    roleAssignments?: IRoleAssignmentPaged[] | IRoleAssignmentDetails[];
    userId: string;
    context: number;
    addAction: IAddAction;
    systemRolesOnly: boolean;
}

const AssignmentMenu: React.FC<IProps> = ({
    groupId,
    page,
    userId,
    context,
    addAction,
    creditAssignments,
    roleAssignments,
    systemRolesOnly,
}: IProps) => {
    const [options, setOptions] = useState<ICascaderOptions[]>([]);
    const [groupName, setGroupName] = useState("");
    const [key, setKey] = useState(Date.now());
    const rolesQuery = appQueryClient.getQueryData<IRolePayload>([QueryKeys.Roles]);
    const definitionsQuery = appQueryClient.getQueryData<IActivityDefinitions>([QueryKeys.ActivityDefinitions]);

    const { data: allGroups } = useQuery<IUserGroupDetails[]>(
        [QueryKeys.UserGroups],
        async () => {
            const getSystem = hasCapability("UserGroup/GetSystem", SystemContext);
            const service = new UserGroupService();
            const groupsResult = getSystem
                ? await service.getSystem()
                : await service.getUser(SessionManager.getServiceUser()?.userId);

            if (!groupsResult.hasErrors) {
                return groupsResult.value;
            }
            return [];
        },
        {
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            enabled: !!SessionManager.getServiceUser()?.userId,
        }
    );

    /**
     *  Get roles where capabilities allow and they don't currently exist for the user.
     */
    const getRoleAddOptions = (newOptions: ICascaderOptions[]) => {
        const roleOptions: ICascaderOptions[] = [];
        const currentRoles = (roleAssignments ?? [])
            ?.filter(
                (x) =>
                    x.contextId === SystemContext ||
                    (x.contextId === GroupContext && x.instanceIds.map((y) => y.id).includes(groupId))
            )
            .map((x) => x.roleId);

        const filteredRoles = rolesQuery?.roles?.filter(
            (x) => !currentRoles.includes(x.roleId) && (systemRolesOnly ? x.contextId === SystemContext : true)
        );
        (filteredRoles ?? []).forEach((role) => {
            if (
                hasCapability(
                    "Role/Assign",
                    role.contextId,
                    role.contextId == SystemContext ? getSystemId() : role.contextId == GroupContext ? groupId : userId
                )
            ) {
                roleOptions.push({
                    value: role.roleId,
                    label: role.name,
                });
            }
        });
        if (roleOptions.length > 0) {
            newOptions.push({
                value: "roles",
                label: "Roles",
                children: roleOptions,
            });
        }
        return newOptions;
    };

    const getCreditCreationOptions = (newOptions: ICascaderOptions[]) => {
        if (creditAssignments?.length === 0) {
            if (
                hasCapability(
                    context === UserContext ? "Credit/CreateUser" : "Credit/CreateGroup",
                    context,
                    context === UserContext ? userId : groupId
                )
            ) {
                newOptions.push({
                    value: "credit",
                    label: "Credit account",
                });
            }
        }
        return newOptions;
    };

    const getLicenseCreationOptions = (newOptions: ICascaderOptions[]) => {
        if (hasCapability("License/Create", context, context === UserContext ? userId : groupId)) {
            const productOptions: ICascaderOptions[] = [];
            definitionsQuery?.products?.forEach((product) => {
                productOptions.push({
                    value: product.productId,
                    label: product.label,
                });
            });

            newOptions.push({
                value: "license",
                label: "Product",
                children: productOptions,
            });
        }
        return newOptions;
    };

    const getAddressCreationOptions = (newOptions: ICascaderOptions[]) => {
        if (
            hasCapability(
                context === UserContext ? "Address/CreateUser" : "Address/CreateGroup",
                context,
                context === UserContext ? userId : groupId
            )
        ) {
            newOptions.push({
                value: "address",
                label: "Address",
            });
        }
        return newOptions;
    };

    const getGroupCreationOptions = (newOptions: ICascaderOptions[], groupId?: string) => {
        if (hasCapability("UserGroup/Create", GroupContext, groupId)) {
            newOptions.push({
                value: "group",
                label: "Group",
                children: [
                    {
                        value: "inputGroup",
                        label: "Input",
                    },
                ],
            });
        }
        return newOptions;
    };

    const getGroupMembershipOptions = (newOptions: ICascaderOptions[]) => {
        newOptions.push({
            value: "assignment",
            label: "Add to group",
            children: getGroupOptions(allGroups),
        });
        return newOptions;
    };

    // Gather menu options appropriate to context.
    const onDropdownVisibleChange = (open: boolean) => {
        if (!open) {
            return;
        }

        let newOptions: ICascaderOptions[] = [];
        switch (context) {
            case SystemContext:
                newOptions = getGroupCreationOptions(newOptions, groupId);
                break;
            case GroupContext:
                newOptions = getGroupCreationOptions(newOptions, groupId);
                newOptions = getAddressCreationOptions(newOptions);
                newOptions = getCreditCreationOptions(newOptions);
                newOptions = getLicenseCreationOptions(newOptions);
                break;
            case UserContext:
                newOptions = getRoleAddOptions(newOptions);
                newOptions = getAddressCreationOptions(newOptions);
                newOptions = getCreditCreationOptions(newOptions);
                newOptions = getLicenseCreationOptions(newOptions);
                newOptions = getGroupMembershipOptions(newOptions);
                break;
        }
        setOptions(newOptions);
    };

    const onChange = (value: string[]) => {
        switch (value[0]) {
            case "roles":
                const rolePayload: IRoleAssignmentPaged = {
                    roleId: value[1],
                    userId: userId,
                    instanceIds: [{ id: groupId }],
                    page: page,
                };
                addAction(TagType.Role, rolePayload);
                break;

            case "credit":
                if (context === GroupContext) {
                    const creditPayload: ICreditDetailsPaged = {
                        contextId: GroupContext,
                        ownerId: SessionManager.getServiceUser()?.userId,
                        instanceId: groupId,
                        creditValue: 0,
                        drawDownValue: DefaultDrawDowns,
                        renewalDay: new Date().getDate(),
                        monthlyDrawDowns: DefaultDrawDowns,
                        page: 0,
                    };
                    addAction(TagType.CreditGroup, creditPayload);
                } else {
                    const creditPayload: ICreditDetailsPaged = {
                        contextId: UserContext,
                        ownerId: SessionManager.getServiceUser()?.userId,
                        instanceId: userId,
                        creditValue: 0,
                        drawDownValue: DefaultDrawDowns,
                        renewalDay: new Date().getDate(),
                        monthlyDrawDowns: DefaultDrawDowns,
                        page: page,
                    };
                    addAction(TagType.CreditUser, creditPayload);
                }
                break;

            case "address":
                if (context === GroupContext) {
                    const addressPayload: IAddressDetailsPaged = {
                        contextId: GroupContext,
                        ownerId: SessionManager.getServiceUser()?.userId,
                        instanceId: groupId,
                        city: "",
                        countryCode: "",
                        county: "",
                        line1: "",
                        line2: "",
                        line3: "",
                        postCode: "",
                        email: "",
                        phone: "",
                        page: 0,
                    };
                    addAction(TagType.AddressGroup, addressPayload);
                } else {
                    const addressPayload: IAddressDetailsPaged = {
                        contextId: UserContext,
                        ownerId: userId,
                        instanceId: userId,
                        city: "",
                        countryCode: "",
                        county: "",
                        line1: "",
                        line2: "",
                        line3: "",
                        postCode: "",
                        email: "",
                        phone: "",
                        page: 0,
                    };
                    addAction(TagType.AddressUser, addressPayload);
                }
                break;

            case "license":
                if (context === GroupContext) {
                    const licensePayload: ILicenseCreationDetails = {
                        contextId: context,
                        instanceId: groupId,
                        ownerId: SessionManager.getServiceUser()?.userId,
                        productId: value[1],
                    };
                    addAction(TagType.LicenseGroup, licensePayload);
                } else {
                    const licensePayload: ILicenseCreationDetails = {
                        contextId: context,
                        instanceId: userId,
                        ownerId: SessionManager.getServiceUser()?.userId,
                        productId: value[1],
                    };
                    addAction(TagType.LicenseUser, licensePayload);
                }
                break;

            case "group":
                break;

            case "assignment":
                const assignmentPayload: IGroupAssignmentDetails = {
                    groupId: value[value.length - 1], // support nested groups
                    userIds: [userId],
                };
                addAction(TagType.Assignment, assignmentPayload);
                break;
        }
    };

    const handleCreateGroup = (groupName: string) => {
        const groupPayload: IUserGroupDetails = {
            name: groupName,
            parentGroupId: groupId,
            ownerId: SessionManager.getServiceUser()?.userId,
            count: 0,
        };
        addAction(TagType.Group, groupPayload);
    };

    const replaceInputOption = (options: ICascaderOptions[]): ICascaderOptions[] => {
        return options.map((option) => {
            if (option.value === "inputGroup") {
                return {
                    ...option,
                    label: (
                        <Input
                            style={{ width: 200 }}
                            placeholder="Type group name"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onPressEnter={() => {
                                handleCreateGroup(groupName);
                                setGroupName("");
                                setKey(Date.now());
                            }}
                            autoFocus
                        />
                    ),
                };
            }

            if (option.children) {
                return {
                    ...option,
                    children: replaceInputOption(option.children),
                };
            }

            return option;
        });
    };

    return (
        <span
            className="assignment-menu"
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <Cascader
                key={key}
                options={replaceInputOption(options)}
                onDropdownVisibleChange={(value) => {
                    onDropdownVisibleChange(value);
                }}
                value={[]}
                onChange={(value) => {
                    onChange(value as string[]);
                }}
            >
                <a href="#">
                    <PlusCircleOutlined />
                </a>
            </Cascader>
        </span>
    );
};
export default AssignmentMenu;
